/**
 * The quiet click zone.
 *
 * This renders NO interactive ancestor: a plain `div` with an `onClick`, no
 * role and no tabindex. The item's one control is its title, which
 * `CresultcardIdentity` renders as a real button carrying the accessible name,
 * `aria-expanded` and `aria-controls`.
 *
 * That split is the whole fix. The previous affordance wrapped the card in
 * `role="button"`, which put an interactive ancestor above every action,
 * player and disclosure inside it, and then needed a hand-maintained selector
 * list to avoid swallowing their clicks — a list where one missing entry
 * silently ate a click. Here the guard is a data attribute the KIT stamps on
 * every control it builds, so a new control cannot forget to be excluded.
 *
 * Pointer users still get the whole card as a target. Keyboard and assistive
 * users see exactly one control per item.
 */
"use client";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { useCresultcardFocusRegistry } from "./cresultcard.focus";

/** Stamped by the kit on every control; never enumerated by a consumer. */
const CONTROL_SELECTOR = "[data-crc-control]";

interface CresultcardOpenSurfaceProps {
  readonly children: ReactNode;
  readonly itemKey: string;
  readonly onOpen: () => void;
  readonly fill?: boolean;
}

export function CresultcardOpenSurface({
  children,
  itemKey,
  onOpen,
  fill = false,
}: CresultcardOpenSurfaceProps): ReactElement {
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const { register } = useCresultcardFocusRegistry();

  // A LAYOUT effect, and the timing is the whole point: the set restores focus
  // from its own `useLayoutEffect`, and a child's layout effect runs before its
  // parent's. Registering passively meant the remounted surface was not in the
  // map yet when the set went looking for it, so restoration was a silent no-op
  // on every close.
  useLayoutEffect(() => {
    // The set restores focus by KEY, not by a generated instance id and a
    // document query — a query cannot survive a re-keyed list.
    return register(itemKey, surfaceRef.current);
  }, [itemKey, register]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      const target = event.target;
      if (target instanceof Element && target.closest(CONTROL_SELECTOR) != null) {
        return;
      }
      // A reader who just selected a phone number is copying it, not opening
      // the item.
      const selection = window.getSelection();
      if (selection != null && selection.toString().length > 0) {
        return;
      }
      onOpen();
    },
    [onOpen],
  );

  return (
    <div
      data-el="Cresultcard/OpenSurface/Root"
      data-crc-item-key={itemKey}
      data-crc-open
      onClick={handleClick}
      ref={surfaceRef}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        height: fill ? "100%" : undefined,
        minWidth: 0,
      }}
    >
      {children}
    </div>
  );
}
